import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { ArrowLeftIcon, ArrowRightIcon, ClockIcon, CalendarIcon } from '@heroicons/react/solid'

import SEOHead from '../utilities/SEOHead'
import { ArticleSchema, BreadcrumbSchema, FAQPageSchema } from '../utilities/StructuredData'
import MarketingNavBar from '../leevo_ui/NavBar/MarketingNavBar/MarketingNavBar'
import MarketingPageFooter from '../leevo_ui/MarketingPageFooter/MarketingPageFooter'
import SimpleJustifiedCTA from '../leevo_ui/SimpleJustifiedCTA/SimpleJustifiedCTA'
import { getArticleBySlug, getRelatedArticles } from '../../content/blog'
import NotFoundPage from '../ErrorPages/NotFoundPage'

const articleContext = require.context('../../content/blog', false, /\.md$/)
const articleModules = {}
articleContext.keys().forEach((key) => {
  const slug = key.replace('./', '').replace('.md', '')
  const content = articleContext(key)
  articleModules[slug] = content.default || content
})

function extractFAQs(content) {
  const faqs = []
  const faqSection = content.match(/## Frequently Asked Questions[\s\S]*$/i)
  if (faqSection) {
    const questionMatches = faqSection[0].matchAll(/###\s*(.+?)\n\n([\s\S]*?)(?=###|$)/g)
    for (const match of questionMatches) {
      const question = match[1].replace(/^Q:\s*/, '').trim()
      const answer = match[2].trim().split('\n\n')[0]
      if (question && answer) {
        faqs.push({ question, answer })
      }
    }
  }
  return faqs
}

function removeFirstH1(content) {
  return content.replace(/^#\s+.+\n+/, '')
}

const CATEGORY_COLOR = 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'

function RelatedArticleCard({ article }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group flex flex-col h-full p-5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all"
    >
      <span className={`self-start px-2.5 py-1 rounded-full text-xs font-semibold ${CATEGORY_COLOR}`}>
        {article.category}
      </span>
      <h4 className="mt-3 font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-2">
        {article.title}
      </h4>
      <span className="mt-auto pt-3 flex items-center text-sm text-gray-500 dark:text-gray-400">
        <ClockIcon className="w-4 h-4 mr-1" />
        {article.readTime}
      </span>
    </Link>
  )
}

export default function BlogArticlePage() {
  const { slug } = useParams()
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)

  const article = getArticleBySlug(slug)
  const relatedArticles = getRelatedArticles(slug, 3)

  useEffect(() => {
    setLoading(true)
    try {
      if (articleModules[slug]) {
        setContent(articleModules[slug])
      } else {
        setContent('')
      }
    } catch (error) {
      console.error('Error loading article:', error)
      setContent('')
    }
    setLoading(false)
  }, [slug])

  const processedContent = useMemo(() => removeFirstH1(content), [content])

  if (!article) {
    return <NotFoundPage />
  }

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: article.title, path: `/blog/${slug}` }
  ]

  const faqs = extractFAQs(content)

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.description}
        path={`/blog/${slug}`}
        type="article"
        keywords={article.keywords}
        articlePublishedTime={article.datePublished}
        articleModifiedTime={article.dateModified}
        articleSection={article.category}
      />
      <ArticleSchema
        title={article.title}
        description={article.description}
        path={`/blog/${slug}`}
        datePublished={article.datePublished}
        dateModified={article.dateModified}
        keywords={article.keywords}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      {faqs.length > 0 && <FAQPageSchema faqs={faqs} />}

      <div className="min-h-screen bg-white dark:bg-gray-900">
        <MarketingNavBar />

        <main>
          <article>
            <header className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="px-4 py-12 mx-auto max-w-4xl sm:px-6 lg:px-8 lg:py-16">
                <Link
                  to="/blog"
                  className="inline-flex items-center text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 mb-8 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Back to Blog
                </Link>

                <div className="flex items-center gap-4 mb-6">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${CATEGORY_COLOR}`}>
                    {article.category}
                  </span>
                </div>

                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl lg:text-5xl text-balance leading-tight">
                  {article.title}
                </h1>

                <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 text-balance">{article.description}</p>

                <div className="flex items-center gap-6 mt-8 text-sm text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5" />
                    {new Date(article.datePublished).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                  <span className="flex items-center gap-2">
                    <ClockIcon className="w-5 h-5" />
                    {article.readTime} read
                  </span>
                </div>
              </div>
            </header>

            <div className="px-4 py-12 mx-auto max-w-4xl sm:px-6 lg:px-8">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                </div>
              ) : (
                <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-a:text-purple-600 dark:prose-a:text-purple-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-purple-600 dark:prose-code:text-purple-400 prose-blockquote:border-purple-500 prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300">
                  <ReactMarkdown>{processedContent}</ReactMarkdown>
                </div>
              )}

              <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
                  <Link
                    to="/onboarding"
                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-semibold text-lg shadow-lg shadow-purple-500/20"
                  >
                    Start Your Free Trial
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Link>
                  <Link
                    to="/blog"
                    className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors"
                  >
                    Browse More Articles →
                  </Link>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-16 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Related Articles</h2>
                <div className="grid gap-6 sm:grid-cols-3">
                  {relatedArticles.map((relatedArticle) => (
                    <RelatedArticleCard key={relatedArticle.slug} article={relatedArticle} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <SimpleJustifiedCTA showTrialButton />
        </main>

        <MarketingPageFooter />
      </div>
    </>
  )
}
